import {Fragment, useEffect, useMemo, useState} from "react";
import {Loader} from "rsuite";
import {useNavigate} from "react-router-dom";
import {StationConfigDto, StationConfigItemDto, StationLinkDto, StationNetworkDto, StationReadResultDto} from "../../../api";
import JournalApi from "../../../services/Journal.api";
import {SharedApi} from "../../../services/Shared.api";
import {getApiErrorMessage} from "../../../services/ApiErrorMessage";
import {formatDateTime, toDate} from "./journal.utils";
import StationTransfer from "./StationTransfer";
import StationValue, {Badge} from "./StationValue";
import {ChannelStates, EffectsTable, isChannelStates, LongLines} from "./SettingsLines";

type StationSettingsProps = {
  deviceId: string;
};

type NetworkKind = "wifi" | "lan" | "sim";

const NETWORKS: { kind: NetworkKind; title: string }[] = [
  {kind: "wifi", title: "Wi-Fi"},
  {kind: "lan", title: "Сеть LAN"},
  {kind: "sim", title: "SIM-карта"},
];

// Подключение прямо сейчас — те же данные и те же значки, что в шапке и в списке устройств
type LiveStatus = { online: boolean; wifi: boolean; lan: boolean; sim: boolean };

const ICONS: Record<NetworkKind, { on: string; off: string }> = {
  wifi: {on: "isWiFiConnected.svg", off: "noWiFi.svg"},
  lan: {on: "isEthernetConnected.svg", off: "noEthernet.svg"},
  sim: {on: "hasSimCard.svg", off: "noSimCard.svg"},
};

const iconPath = (fileName: string) => `${process.env.PUBLIC_URL}/${fileName}`;

const liveText = (kind: NetworkKind, live: LiveStatus | null): { text: string; active: boolean } | null => {
  if (!live) return null;
  if (!live.online) return {text: "станция не в сети", active: false};
  if (kind === "sim") return live.sim ? {text: "SIM-карта установлена", active: true} : {text: "SIM-карты нет", active: false};
  return live[kind] ? {text: "подключено сейчас", active: true} : {text: "не подключено", active: false};
};

const NETWORK_TITLES: Record<string, string> = {wifi: "Wi-Fi", lan: "LAN", sim: "SIM"};

const SEEN_FIELD: Record<NetworkKind, "wifiSeenAt" | "lanSeenAt" | "simSeenAt"> = {
  wifi: "wifiSeenAt",
  lan: "lanSeenAt",
  sim: "simSeenAt",
};

// Сверка при открытии раздела — только если последняя была давно (ночью сервер сверяет сам)
const AUTO_CHECK_AFTER_MS = 60 * 60 * 1000;

// «29.10 03:02 · ночная сверка — ничего не изменилось»
const lastCheckText = (link: StationLinkDto): string => {
  const head = `${formatDateTime(link.lastCheckAt)}${link.lastCheckBy ? ` · ${link.lastCheckBy}` : ""}`;
  const changed = link.lastCheckChanged ?? 0;
  const result = changed > 0 ? `изменилось разделов: ${changed}` : "с того момента ничего не изменилось";
  return link.lastCheckOk === false && link.lastCheckError
    ? `${head} — ${result}; не прочитано: ${link.lastCheckError}`
    : `${head} — ${result}`;
};

// «Wi-Fi — без изменений · LAN — обновлено · SIM — не прочитано: станция не ответила вовремя»
const readSummary = (results: StationReadResultDto[]): string =>
  results.map((x) => {
    const title = x.title ?? NETWORK_TITLES[x.kind ?? ""] ?? x.kind;
    if (!x.success) return `${title} — не прочитано: ${x.error ?? "ошибка"}`;
    return `${title} — ${x.changed ? "обновлено" : "без изменений"}`;
  }).join(" · ");

type HistoryTarget = { settingKey: string; deviceId?: string | null };

// Группы снимка — в том же порядке, что на сервере (StationConfigText.GroupOrder)
const GROUP_ORDER = ["Первичные часы", "Вторичные часы", "Реле", "Корректировка восхода/заката", "Музыка", "Блокировки", "Устройства BIGLine", "Связь"];
const DEVICES_GROUP = "Устройства BIGLine";

type SettingsBlock = { key: string; label: string; items: StationConfigItemDto[] };

const SERIAL_KEY = "Devices.SerialNumber|";
type SettingsGroup = { group: string; count: number; blocks: SettingsBlock[] };

const settingsWord = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "настройка";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "настройки";
  return "настроек";
};

const historyId = (target: HistoryTarget) => `${target.deviceId ?? ""}|${target.settingKey}`;

// «2026-10-28T12:30» из поля даты → Date (местное время)
const parseLocal = (value: string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

// Под заголовком устройства его название в начале описания не повторяем
const withoutDevice = (item: StationConfigItemDto): string => {
  const text = item.description ?? "";
  const prefix = item.deviceLabel ? `${item.deviceLabel}: ` : "";
  if (!prefix || !text.startsWith(prefix)) return text;
  const rest = text.slice(prefix.length);
  return rest.charAt(0).toUpperCase() + rest.slice(1);
};

// Название поля и значение: как на сервере — до первого «: » или « — »
const splitField = (item: StationConfigItemDto): { label: string; value: string } => {
  const text = withoutDevice(item);
  const colon = text.indexOf(": ");
  const dash = text.indexOf(" — ");
  const index = colon >= 0 && (dash < 0 || colon < dash) ? colon : dash;
  if (index <= 0) return {label: text, value: item.value ?? ""};
  const separator = index === colon ? 2 : 3;
  return {label: text.slice(0, index), value: text.slice(index + separator) || (item.value ?? "")};
};

// Переключатель хода механизма (блокировка администратора) — ключ в снимке настроек
const MOVEMENT_KEY = "Mechanism.SetMovementPermissionAsync";

// Карточка устройства → его id (ключ карточки «device:<id>»)
const blockDeviceId = (key: string) => (key.startsWith("device:") ? key.slice("device:".length) : null);

// «Эффекты для события «в 0 часов»» — строки эффектов диммера собираются в одну таблицу
const EFFECTS_LABEL = /^Эффекты для события «(.+)»$/i;

// Список «a; b; c» показываем по строкам
const valueLines = (value: string) => value.split("; ").map((x) => x.trim()).filter(Boolean);

const latestOf = (items: StationConfigItemDto[]) =>
  items.reduce<StationConfigItemDto | undefined>((best, item) =>
    !best || (toDate(item.updatedAt)?.getTime() ?? 0) > (toDate(best.updatedAt)?.getTime() ?? 0) ? item : best, undefined);

// Подпись без повтора заголовка карточки: в «Блоке управления» — «Светодиод», а не «Светодиод блока управления»
const CARD_SUFFIXES: Record<string, string> = {
  "блок управления": " блока управления",
  "часовая станция": " часовой станции",
};

type FieldRow = { key: string; label: string; value: string; items: StationConfigItemDto[]; range?: [string, string] };

// Поля карточки: короткие подписи и пары «время отключения/включения» одной строкой
const buildRows = (items: StationConfigItemDto[], card: string): FieldRow[] => {
  const rows: FieldRow[] = items.map((item) => ({key: item.id ?? "", ...splitField(item), items: [item]}));
  const suffix = CARD_SUFFIXES[card.toLowerCase()];
  if (suffix) {
    const short = rows.map((row) => row.label.endsWith(suffix) ? row.label.slice(0, -suffix.length) : row.label);
    rows.forEach((row, i) => {
      if (short.filter((x) => x.toLowerCase() === short[i].toLowerCase()).length === 1) row.label = short[i];
    });
  }
  const result: FieldRow[] = [];
  const used = new Set<FieldRow>();
  rows.forEach((row) => {
    if (used.has(row)) return;
    const off = row.label.match(/^Время отключения (.+)$/i);
    const pair = off && rows.find((x) => !used.has(x) && x.label.toLowerCase() === `время включения ${off[1]}`.toLowerCase());
    if (off && pair) {
      used.add(pair);
      result.push({key: row.key, label: `Отключение ${off[1]}`, value: "", items: [...row.items, ...pair.items], range: [row.value, pair.value]});
      return;
    }
    const on = row.label.match(/^Время включения (.+)$/i);
    const pairOff = on && rows.find((x) => !used.has(x) && x.label.toLowerCase() === `время отключения ${on[1]}`.toLowerCase());
    if (on && pairOff) {
      used.add(pairOff);
      result.push({key: row.key, label: `Отключение ${on[1]}`, value: "", items: [...pairOff.items, ...row.items], range: [pairOff.value, row.value]});
      return;
    }
    result.push(row);
  });
  return result;
};

const SECONDARY_GROUP = "Вторичные часы";

type ChannelRow = { number: number; state?: string; sync?: boolean; impulse?: string; model?: string; serial?: string; place?: string };

// Таблица каналов вторичных часов: состояние, синхронизация, импульс и данные механизма
const buildChannels = (items: StationConfigItemDto[]) => {
  const rows = new Map<number, ChannelRow>();
  const row = (number: number) => {
    if (!rows.has(number)) rows.set(number, {number});
    return rows.get(number)!;
  };
  let mode: string | undefined;
  const used = new Set<string>();
  for (const item of items) {
    const key = item.settingKey ?? "";
    const {label, value} = splitField(item);
    if (key.startsWith("ClockStation.SetChannelImpulse#ChannelsStateModel")) {
      used.add(item.id ?? "");
      for (const line of valueLines(value)) {
        const channel = /^канал (\d+) — (\S+)(.*)$/.exec(line);
        if (channel) {
          const r = row(Number(channel[1]));
          r.state = channel[2] === "включён" ? "да" : "нет";
          r.sync = channel[2] === "включён" ? !channel[3].includes("без синхронизации") : undefined;
        } else if (line.startsWith("режим каналов — ")) {
          const name = line.slice("режим каналов — ".length);
          mode = name.charAt(0).toUpperCase() + name.slice(1);
        }
      }
    } else if (key.startsWith("ClockStation.SetChannelImpulse#ImpulseModel")) {
      const number = /канала (\d+)/.exec(label);
      if (number) {
        used.add(item.id ?? "");
        row(Number(number[1])).impulse = value;
      }
    } else if (key.startsWith("Mechanisms.UpdateMechanismDataAsync")) {
      const number = /канала (\d+)/.exec(label);
      const data = /^модель (.*), серийный номер (.*), место (.*)$/.exec(value);
      if (number && data) {
        used.add(item.id ?? "");
        const r = row(Number(number[1]));
        r.model = data[1];
        r.serial = data[2];
        r.place = data[3];
      }
    }
  }
  return {mode, rows: Array.from(rows.values()).sort((a, b) => a.number - b.number), used};
};

const metaText = (updatedAt?: string | null, userLogin?: string | null) =>
  [formatDateTime(updatedAt), userLogin].filter(Boolean).join(" · ");

const downloadJson = (data: unknown, fileName: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {type: "application/json"});
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// История версий одной настройки (раскрывается под строкой)
const History = ({deviceId, target}: { deviceId: string; target: HistoryTarget }) => {
  const [items, setItems] = useState<StationConfigItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    JournalApi.getStationConfigHistory(deviceId, target.settingKey, target.deviceId)
      .then((result) => {
        if (!cancelled) setItems(result ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, "Не удалось загрузить историю"));
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, target.settingKey, target.deviceId]);

  if (error) return <div className="station-settings__history station-settings__history--error">{error}</div>;
  if (!items) return <div className="station-settings__history"><Loader size="xs" content="Загрузка…"/></div>;

  return (
    <ol className="station-settings__history">
      {items.map((item, index) => (
        <li key={item.id} className="station-settings__version">
          <span className="station-settings__version-meta">
            {metaText(item.updatedAt, item.userLogin)}
            {index === 0 && <em className="station-settings__current">текущее</em>}
          </span>
          <span className="station-settings__version-text">{item.description}</span>
        </li>
      ))}
    </ol>
  );
};

type NetworkCardProps = {
  kind: NetworkKind;
  title: string;
  network?: StationNetworkDto | null;
  stationId: string;
  openHistory: string | null;
  toggleHistory: (target: HistoryTarget) => void;
  live: LiveStatus | null;
  link?: StationLinkDto | null;
};

// Пароль Wi-Fi или SIM: скрыт, по кнопке «показать» запрашивается у сервера (только админ)
const Password = ({stationId, kind, network}: { stationId: string; kind: NetworkKind; network: StationNetworkDto }) => {
  const [password, setPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPassword(null);
    setError(null);
  }, [network.id]);

  if (!network.hasPassword) return <>не задан</>;

  const toggle = () => {
    if (password !== null) {
      setPassword(null);
      return;
    }
    setLoading(true);
    setError(null);
    JournalApi.getNetworkPassword(stationId, kind, network.id)
      .then((result) => {
        if (result?.password == null) setError("Не удалось расшифровать пароль");
        else setPassword(result.password);
      })
      .catch((err) => setError(getApiErrorMessage(err, "Не удалось получить пароль")))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <code>{password ?? "••••••••"}</code>
      <button type="button" className="station-settings__link" onClick={toggle} disabled={loading}>
        {loading ? "…" : password !== null ? "скрыть" : "показать"}
      </button>
      {error && <small className="station-network__error">{error}</small>}
    </>
  );
};

// Карточка сети: Wi-Fi (имя сети, пароль, адрес), LAN (адрес) или SIM (APN, логин, пароль)
const NetworkCard = ({kind, title, network, stationId, openHistory, toggleHistory, live, link}: NetworkCardProps) => {
  const target = {settingKey: `network:${kind}`};
  const isOpen = openHistory === historyId(target);
  const liveState = liveText(kind, live);
  const active = Boolean(liveState?.active);
  // Станция не в сети — отмечаем канал, через который она была на связи последний раз
  const wasLast = live?.online === false && link?.lastChannel === kind;
  const state = wasLast ? {text: "последнее подключение", active: false} : liveState;
  const seenAt = link?.[SEEN_FIELD[kind]];

  return (
    <section className={`station-network${active ? " station-network--active" : ""}${wasLast ? " station-network--last" : ""}`}>
      <header className="station-network__head">
        <h3>
          <img src={iconPath(active ? ICONS[kind].on : ICONS[kind].off)} alt="" className="station-network__icon"/>
          {title}
        </h3>
        {state && (
          <span className={`station-network__state${active ? " station-network__state--on" : ""}${wasLast ? " station-network__state--last" : ""}`}>{state.text}</span>
        )}
      </header>

      {!network && <p className="station-network__empty">На сервере пока нет данных</p>}

      {network && (
        <dl className="station-network__fields">
          {kind === "wifi" && (
            <>
              <dt>Сеть (логин)</dt>
              <dd>{network.ssid || "—"}</dd>
              <dt>Пароль</dt>
              <dd className="station-network__password"><Password stationId={stationId} kind={kind} network={network}/></dd>
            </>
          )}
          {kind === "sim" && (
            <>
              <dt>APN</dt>
              <dd>{network.apn || "—"}</dd>
              <dt>Логин</dt>
              <dd>{network.login || "—"}</dd>
              <dt>Пароль</dt>
              <dd className="station-network__password"><Password stationId={stationId} kind={kind} network={network}/></dd>
            </>
          )}
          {kind !== "sim" && (
            <>
              <dt>Адрес</dt>
              <dd>{network.dhcpEnabled ? "автоматически (DHCP)" : "вручную"}</dd>
            </>
          )}
          {kind !== "sim" && !network.dhcpEnabled && (
            <>
              <dt>IP</dt>
              <dd>{network.ip || "—"}</dd>
              <dt>Маска</dt>
              <dd>{network.mask || "—"}</dd>
              <dt>Шлюз</dt>
              <dd>{network.gateway || "—"}</dd>
              <dt>DNS</dt>
              <dd>{network.dns || "—"}</dd>
            </>
          )}
        </dl>
      )}

      {(network || seenAt) && (
        <p className="station-network__since">
          {network && <>Без изменений с {formatDateTime(network.updatedAt)}{network.userLogin && <> · {network.userLogin}</>}</>}
          {network && link?.lastCheckAt && <><br/>Последняя проверка: {formatDateTime(link.lastCheckAt)}{link.lastCheckBy && <> · {link.lastCheckBy}</>}</>}
          {seenAt && <>{network && <br/>}Последний раз на связи через {title}: {formatDateTime(seenAt)}</>}
        </p>
      )}

      {network && (
        <button type="button" className="station-settings__link" onClick={() => toggleHistory(target)}>
          {isOpen ? "Скрыть историю" : `История (${network.versions ?? 1})`}
        </button>
      )}
      {network && isOpen && <History deviceId={stationId} target={target}/>}
    </section>
  );
};

// Раздел «Настройки станции»: снимок всех настроек, собирается автоматически при каждом сохранении
const StationSettings = ({deviceId}: StationSettingsProps) => {
  const [config, setConfig] = useState<StationConfigDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atInput, setAtInput] = useState("");
  const [reload, setReload] = useState(0);
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [readResult, setReadResult] = useState<{ at: Date; text: string; failed: boolean } | null>(null);
  const [readTick, setReadTick] = useState(0);
  const [live, setLive] = useState<LiveStatus | null>(null);
  const [autoChecked, setAutoChecked] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const navigate = useNavigate();
  // Скрыть выключенные каналы и «Без эффекта»
  const [onlyEnabled, setOnlyEnabled] = useState(false);

  // Текущее подключение станции — как значки в шапке
  useEffect(() => {
    let cancelled = false;
    SharedApi.getControlBlocksStatus(deviceId)
      .then((result) => {
        if (cancelled) return;
        setLive({
          online: Boolean(result?.isConnected),
          wifi: Boolean(result?.status?.isWiFiConnected),
          lan: Boolean(result?.status?.isEthernetConnected),
          sim: Boolean(result?.status?.hasSimCard),
        });
      })
      .catch(() => {
        if (!cancelled) setLive(null);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, readTick]);

  const at = useMemo(() => parseLocal(atInput), [atInput]);

  // Снимок всегда берём с сервера и показываем сразу — даже если станция не в сети
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    JournalApi.getStationConfig(deviceId, at)
      .then((result) => {
        if (!cancelled && result) setConfig(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setConfig(null);
          setError(getApiErrorMessage(err, "Не удалось загрузить настройки станции"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, at, reload]);

  // Фоном сверяем сеть со станцией (Wi-Fi, LAN, SIM); если что-то изменилось — сервер сохранит новую версию,
  // и снимок перечитается. Экран при этом не ждёт станцию
  useEffect(() => {
    if (at || readTick === 0) return undefined;
    let cancelled = false;
    setReading(true);
    JournalApi.readFromStation(deviceId)
      .then((results) => {
        if (cancelled) return;
        setReadResult({
          at: new Date(),
          text: readSummary(results ?? []),
          failed: (results ?? []).some((x) => !x.success),
        });
        setReload((x) => x + 1);
      })
      .catch((err) => {
        if (!cancelled) {
          setReadResult({at: new Date(), text: getApiErrorMessage(err, "Не удалось прочитать станцию"), failed: true});
        }
      })
      .finally(() => {
        if (!cancelled) setReading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, at, readTick]);

  // При первом открытии сверяем со станцией, если она в сети и последняя сверка была больше часа назад
  useEffect(() => {
    if (autoChecked || at || !config || !live) return;
    setAutoChecked(true);
    const last = toDate(config.link?.lastCheckAt);
    if (live.online && (!last || Date.now() - last.getTime() > AUTO_CHECK_AFTER_MS)) setReadTick((x) => x + 1);
  }, [autoChecked, at, config, live]);

  // Группа (первичные часы, вторичные часы, реле, музыка, устройства BIGLine, связь) → раздел или устройство → настройки
  const groups = useMemo(() => {
    const result: SettingsGroup[] = [];
    for (const item of config?.settings ?? []) {
      const groupName = item.group || "Прочее";
      let group = result.find((x) => x.group === groupName);
      if (!group) {
        group = {group: groupName, count: 0, blocks: []};
        result.push(group);
      }
      // Устройства BIGLine — отдельная карточка на каждое (их может быть несколько одного типа)
      const isDevice = groupName === DEVICES_GROUP && Boolean(item.deviceId);
      const settingKey = item.settingKey ?? "";
      const isUsb = settingKey.startsWith("ControlBlocks.SetUsb") || settingKey.startsWith("ControlBlocks.SetModemLocationAsync");
      // Питание USB и модем — отдельной карточкой, как блок «Настройка USB» на странице «Первичные часы»
      const section = isUsb ? "Настройка USB" : item.section || "Прочее";
      const key = isDevice ? `device:${item.deviceId}` : `section:${section}`;
      const label = isDevice ? item.deviceLabel || "Устройство" : section;
      let block = group.blocks.find((x) => x.key === key);
      if (!block) {
        block = {key, label, items: []};
        group.blocks.push(block);
      }
      block.items.push(item);
      group.count += 1;
    }
    const order = (name: string) => {
      const index = GROUP_ORDER.indexOf(name);
      return index < 0 ? GROUP_ORDER.length : index;
    };
    // Устройства — в порядке списка (тип, адрес), серийный номер — первым полем
    const deviceOrder = (config?.devices ?? []).map((x) => `device:${x.deviceId}`);
    for (const group of result) {
      if (group.group !== DEVICES_GROUP) continue;
      group.blocks.sort((a, b) => deviceOrder.indexOf(a.key) - deviceOrder.indexOf(b.key));
      for (const block of group.blocks) {
        block.items.sort((a, b) =>
          Number((b.settingKey ?? "").startsWith(SERIAL_KEY)) - Number((a.settingKey ?? "").startsWith(SERIAL_KEY)));
      }
    }
    return result.sort((a, b) => order(a.group) - order(b.group));
  }, [config]);

  const deviceSummary = useMemo(
    () => (config?.devices ?? [])
      .map((x) => (x.serialNumber ? `${x.label} (№ ${x.serialNumber})` : x.label))
      .filter(Boolean)
      .join(" · "),
    [config],
  );

  const toggleHistory = (target: HistoryTarget) => {
    const id = historyId(target);
    setOpenHistory((current) => (current === id ? null : id));
  };

  const exportFile = () => {
    setExporting(true);
    setExportError(null);
    JournalApi.exportStationConfig(deviceId, at)
      .then((data) => {
        const stamp = (at ?? new Date()).toISOString().slice(0, 16).replace(/[:T]/g, "-");
        downloadJson(data, `station-${deviceId.slice(-12)}-settings-${stamp}.json`);
      })
      .catch((err) => setExportError(getApiErrorMessage(err, "Не удалось выгрузить настройки")))
      .finally(() => setExporting(false));
  };

  const isEmpty = config && (config.settings ?? []).length === 0;

  return (
    <div className="station-settings">
      <div className="station-settings__toolbar">
        <label className="station-settings__at">
          Состояние на
          <input type="datetime-local" value={atInput} onChange={(e) => setAtInput(e.target.value)}/>
          {atInput && (
            <button type="button" className="station-settings__link" onClick={() => setAtInput("")}>сейчас</button>
          )}
        </label>
        <label className="station-settings__only">
          <input type="checkbox" checked={onlyEnabled} onChange={(e) => setOnlyEnabled(e.target.checked)}/>
          Только включённое
        </label>
        <div className="station-settings__actions">
          <button type="button" className="journal__refresh" onClick={() => (at ? setReload((x) => x + 1) : setReadTick((x) => x + 1))}
                  disabled={reading && !at}>
            {at ? "Обновить" : reading ? "Сверяю со станцией…" : "Сверить со станцией"}
          </button>
          <button type="button" className="station-settings__export" onClick={exportFile} disabled={exporting}>
            {exporting ? "Выгрузка…" : "Скачать файл настроек"}
          </button>
          {!at && (
            <button type="button" className="station-settings__export station-settings__export--outline"
                    onClick={() => setTransferOpen((x) => !x)}>
              {transferOpen ? "Закрыть перенос" : "Перенос настроек"}
            </button>
          )}
        </div>
      </div>

      {transferOpen && !at && (
        <StationTransfer stationId={deviceId} online={live?.online ?? null}
                         onClose={() => setTransferOpen(false)} onDone={() => setReload((x) => x + 1)}/>
      )}

      <p className="journal__hint station-settings__intro">
        Настройки хранятся на сервере и показываются, даже если станция не в сети. Каждую ночь в 03:00 сервер сверяет
        их со станцией: первичные часы (часовой пояс, синхронизация, USB), вторичные часы (каналы, импульс, подписи
        механизмов), реле, музыку (куранты), Wi-Fi, LAN, SIM и все устройства BIGLine; кроме того, настройки
        записываются при каждом сохранении через приложение. Если станция выйдет из строя, их можно перенести на новую
        кнопкой «Перенос настроек». Новая версия появляется,
        только если что-то изменилось, повторы удаляются. Хранится вся история.
        {config?.updatedAt && <> Последнее изменение: <b>{formatDateTime(config.updatedAt)}</b>.</>}
        {" "}В файле настроек пароли Wi-Fi и SIM указаны открытым текстом — храните его только у себя.
      </p>
      {!at && live && !live.online && config?.link?.lastChannelAt && (
        <div className="station-settings__read station-settings__read--warn">
          Станция не в сети. Последний раз была на связи {formatDateTime(config.link.lastChannelAt)}
          {config.link.lastChannel && <> через <b>{NETWORK_TITLES[config.link.lastChannel] ?? config.link.lastChannel}</b></>}.
        </div>
      )}
      {!at && !reading && !readResult && config?.link?.lastCheckAt && (
        <div className={`station-settings__read${config.link.lastCheckOk === false ? " station-settings__read--warn" : ""}`}>
          Последняя сверка со станцией: {lastCheckText(config.link)}
        </div>
      )}
      {reading && <div className="station-settings__read"><Loader size="xs" content="Показаны данные с сервера. Сверяю со станцией сеть, часовую станцию и все устройства BIGLine…"/></div>}
      {!reading && readResult && !at && (
        <div className={`station-settings__read${readResult.failed ? " station-settings__read--warn" : ""}`}>
          Сверено со станцией в {formatDateTime(readResult.at)}: {readResult.text}
          {readResult.failed && <>. Ниже — последние сохранённые на сервере данные.</>}
        </div>
      )}
      {exportError && <div className="journal__state journal__state--error">{exportError}</div>}

      {loading && !config && <div className="journal__state"><Loader content="Загрузка…"/></div>}
      {!loading && error && <div className="journal__state journal__state--error">{error}</div>}

      {config && (
        <>
          <div className="station-settings__networks">
            {NETWORKS.map((item) => (
              <NetworkCard key={item.kind} kind={item.kind} title={item.title} network={config[item.kind]}
                           stationId={deviceId} openHistory={openHistory} toggleHistory={toggleHistory}
                           live={at ? null : live} link={at ? null : config.link}/>
            ))}
          </div>

          {isEmpty && (
            <div className="journal__state">
              {at
                ? "На выбранный момент других сохранённых настроек нет"
                : "Остальные настройки появятся здесь после сверки со станцией или первого сохранения через приложение"}
            </div>
          )}

          {groups.map((group) => (
            <details key={group.group} className="station-settings__group" open>
              <summary className="station-settings__group-title">
                <span>{group.group}</span>
                <span className="station-settings__group-count">{group.count} {settingsWord(group.count)}</span>
              </summary>
              {group.group === DEVICES_GROUP && deviceSummary && (
                <div className="station-settings__devices">Подключены: {deviceSummary}</div>
              )}
              <div className="station-settings__cards">
                {group.blocks.map((block) => {
                  const channels = group.group === SECONDARY_GROUP ? buildChannels(block.items) : null;
                  const fields = block.items.filter((item) => !channels?.used.has(item.id ?? ""));
                  const tableItems = block.items.filter((item) => channels?.used.has(item.id ?? ""));
                  const latest = latestOf(block.items);
                  return (
                    <div key={block.key} className="station-settings__card">
                      {(group.blocks.length > 1 || group.group === DEVICES_GROUP) && (
                        blockDeviceId(block.key)
                          ? (
                            <button type="button" className="station-settings__card-title station-settings__card-title--link"
                                    title="Открыть устройство в разделе «Устройства BIGLine»"
                                    onClick={() => navigate(`/devices/${deviceId}?tab=devices&externalId=${blockDeviceId(block.key)}`)}>
                              {block.label} <span>→</span>
                            </button>
                          )
                          : <div className="station-settings__card-title">{block.label}</div>
                      )}
                      {channels && channels.mode && (
                        <dl className="station-settings__fields">
                          <dt>Режим каналов</dt>
                          <dd><span className="station-value__badge station-value__badge--off">{channels.mode}</span></dd>
                        </dl>
                      )}
                      {channels && channels.rows.length > 0 && (
                        <div className="station-settings__table-wrap">
                          <table className="station-settings__table">
                            <thead>
                            <tr>
                              <th>Канал</th>
                              <th>Состояние</th>
                              <th>Синхр.</th>
                              <th>Импульс</th>
                              <th>Модель</th>
                              <th>Место</th>
                            </tr>
                            </thead>
                            <tbody>
                            {channels.rows.map((r) => (
                              <tr key={r.number}>
                                <td>{r.number}</td>
                                <td>{r.state ? <Badge tone={/^(да|работ|включ)/i.test(r.state) ? "on" : "off"}>
                                  {/^да$/i.test(r.state) ? "работает" : /^нет$/i.test(r.state) ? "стоит" : r.state}
                                </Badge> : <span className="station-value__empty">—</span>}</td>
                                <td>{r.sync === undefined ? <span className="station-value__empty">—</span>
                                  : <Badge tone={r.sync ? "on" : "off"}>{r.sync ? "да" : "нет"}</Badge>}</td>
                                <td>{r.impulse ?? "—"}</td>
                                <td title={r.serial && r.serial !== "—" ? `Серийный номер ${r.serial}` : undefined}>
                                  {r.model ?? "—"}
                                </td>
                                <td>{r.place ?? "—"}</td>
                              </tr>
                            ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {fields.length > 0 && (() => {
                        const allRows = buildRows(fields, block.label);
                        const effectRows = allRows.filter((row) => EFFECTS_LABEL.test(row.label));
                        const plainRows = allRows.filter((row) => !EFFECTS_LABEL.test(row.label));
                        const historyLink = (item: StationConfigItemDto, name = "") => {
                          const target = {settingKey: item.settingKey ?? "", deviceId: item.deviceId};
                          const isOpen = openHistory === historyId(target);
                          return (
                            <button key={item.id} type="button" className="station-settings__link"
                                    onClick={() => toggleHistory(target)}>
                              {isOpen ? "скрыть" : `${name}история (${item.versions})`}
                            </button>
                          );
                        };
                        const openEffect = effectRows.flatMap((row) => row.items).find((item) =>
                          openHistory === historyId({settingKey: item.settingKey ?? "", deviceId: item.deviceId}));
                        return (
                        <dl className="station-settings__fields">
                          {plainRows.map((row) => {
                            const lines = valueLines(row.value);
                            const withHistory = row.items.filter((item) => (item.versions ?? 0) > 1);
                            const openItem = row.items.find((item) =>
                              openHistory === historyId({settingKey: item.settingKey ?? "", deviceId: item.deviceId}));
                            return (
                              <Fragment key={row.key}>
                                <dt>{row.label.charAt(0).toUpperCase() + row.label.slice(1)}</dt>
                                <dd>
                                  {row.range
                                    ? <span className="station-value__range">{row.range[0] || "—"}<b>→</b>{row.range[1] || "—"}</span>
                                    : isChannelStates(lines)
                                      ? <ChannelStates lines={lines} onlyEnabled={onlyEnabled}/>
                                      : lines.length > 1
                                        ? <LongLines lines={lines} onlyEnabled={onlyEnabled}/>
                                        : <StationValue label={row.label} value={row.value} card={block.label}/>}
                                  {withHistory.map((item) =>
                                    historyLink(item, row.items.length > 1 ? (row.items.indexOf(item) === 0 ? "откл.: " : "вкл.: ") : ""))}
                                </dd>
                                {openItem && (
                                  <div className="station-settings__field-history">
                                    <History deviceId={deviceId} target={{settingKey: openItem.settingKey ?? "", deviceId: openItem.deviceId}}/>
                                  </div>
                                )}
                              </Fragment>
                            );
                          })}
                          {/^Механизм/.test(block.label) && !block.items.some((item) => (item.settingKey ?? "").startsWith(MOVEMENT_KEY)) && (
                            <>
                              <dt>Ход механизма (блокировка администратора)</dt>
                              <dd><span className="station-value__empty">ещё не прочитан — появится после сверки со станцией</span></dd>
                            </>
                          )}
                          {effectRows.length > 0 && (
                            <>
                              <dt className="settings-effects__wide settings-effects__label">Эффекты</dt>
                              <dd className="settings-effects__wide">
                                <EffectsTable
                                  onlyEnabled={onlyEnabled}
                                  rows={effectRows.map((row) => ({
                                    key: row.key,
                                    event: row.label.replace(EFFECTS_LABEL, "$1").replace(/^./, (c) => c.toUpperCase()),
                                    lines: valueLines(row.value),
                                    extra: row.items.filter((item) => (item.versions ?? 0) > 1).map((item) => historyLink(item)),
                                  }))}
                                />
                              </dd>
                              {openEffect && (
                                <div className="station-settings__field-history">
                                  <History deviceId={deviceId} target={{settingKey: openEffect.settingKey ?? "", deviceId: openEffect.deviceId}}/>
                                </div>
                              )}
                            </>
                          )}
                        </dl>
                        );
                      })()}
                      {tableItems.some((item) => (item.versions ?? 0) > 1) && (
                        <div className="station-settings__card-links">
                          История:
                          {tableItems.filter((item) => (item.versions ?? 0) > 1).map((item) => {
                            const target = {settingKey: item.settingKey ?? "", deviceId: item.deviceId};
                            const isOpen = openHistory === historyId(target);
                            return (
                              <Fragment key={item.id}>
                                <button type="button" className="station-settings__link" onClick={() => toggleHistory(target)}>
                                  {splitField(item).label} ({item.versions})
                                </button>
                                {isOpen && <div className="station-settings__field-history"><History deviceId={deviceId} target={target}/></div>}
                              </Fragment>
                            );
                          })}
                        </div>
                      )}
                      {latest && (
                        <div className="station-settings__card-meta">
                          Последнее изменение: {metaText(latest.updatedAt, latest.userLogin)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
        </>
      )}
    </div>
  );
};

export default StationSettings;
